import { useMemo } from 'react';

import { RouterProvider, createRouter } from '@tanstack/react-router';
import { toast } from 'sonner';

import { LaunchScreen } from '@/common/components/modules/launch-screen';
import { AuthProvider } from '@/common/contexts/auth-context';
import { FeatureFlagsProvider } from '@/common/contexts/feature-flags-context';
import { useAuth } from '@/common/hooks/use-auth';
import { useNetworkStatus } from '@/common/hooks/use-network-status';
import { QueryProvider } from '@/common/providers';
import { queryClient } from '@/infra/network/query';
import { SentryErrorBoundary } from '@/infra/observability/sentry';
import { routeTree } from '@/routeTree.gen';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultPendingComponent: () => <LaunchScreen isLoading={true} />,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  context: { auth: undefined!, queryClient },
});
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    crumb?: string;
  }
}

function InnerApp() {
  const auth = useAuth();
  const context = useMemo(
    () => ({ auth, queryClient }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth.user, auth.signedIn],
  );

  useNetworkStatus({
    onOffline: () => {
      toast.error('Sem conexão. Algumas funções podem não estar disponíveis.', {
        duration: 15 * 1000,
      });
    },
    onOnline: () => {
      toast.info('Conexão restaurada!');
    },
  });

  return <RouterProvider router={router} context={context} />;
}

const App = () => {
  return (
    <SentryErrorBoundary>
      <QueryProvider>
        <AuthProvider>
          <FeatureFlagsProvider>
            <InnerApp />
          </FeatureFlagsProvider>
        </AuthProvider>
      </QueryProvider>
    </SentryErrorBoundary>
  );
};

export { App };
